import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  ReportCountryRow,
  ReportFilter,
  ReportRange,
  ReportSales,
  ReportShippingCostRow,
  ReportTopProductRow,
  ReportsService,
} from '../api';

// ============================================================================
// Riportok — értékesítés, profit és szállítási bontás.
// Design: erp-design/screens/12-riportok.html,
// logika: 23-logic.js reportsVals() / topProductsVals() / buildLineChart().
// Elsődleges adatforrás: GET orders/reports/overview (meglévő végpont) —
// rendelésszám, árbevétel, átlag kosár, országok, idővonal.
// A profit, a szállítási átlagköltség és a termék-profitbontás a ReportsService
// (reports/sales, reports/shipping-costs, reports/top-products) végpontjaiból
// jön; amíg azok nem élnek, az érintett kártyák üres állapotot mutatnak.
// ============================================================================

type RangeTab = 'Nap' | 'Hét' | 'Hónap' | 'Év' | 'Egyedi';

/** GET orders/reports/overview válasz (kivonat — orders.service.ts OrdersReportsOverview) */
interface OverviewResponse {
  totals: {
    orders: number;
    revenue: number;
    average_order_value: number;
    currency_code: string | null;
  };
  merchants: Array<{ merchant_id: number; merchant_name: string; order_count: number }>;
  countries: Array<{ country: string; order_count: number; revenue: number; share: number }>;
  timeline: Array<{ date: string; order_count: number; revenue: number }>;
}

interface Bar { l: string; s: number; p: number | null; }
interface Pt { x: number; y: number; }
interface TextEl { x: number; y: number; text: string; }

interface ChartVm {
  grid: Array<{ y: number; label: string; base: boolean }>;
  salesPath: string;
  profitPath: string | null;
  salesDots: Pt[];
  profitDots: Pt[];
  salesLabels: TextEl[];
  pctLabels: TextEl[];
  xLabels: TextEl[];
  last: { x: number; ys: number; yp: number | null } | null;
  hits: Array<{ x: number; w: number }>;
}

interface TooltipVm {
  x: number; sy: number; py: number | null;
  tx: number; ty: number;
  label: string; salesText: string; profitText: string;
}

interface KpiVm {
  icon: 'sales' | 'profit' | 'orders' | 'basket';
  label: string;
  value: string;
  delta: string;
  tone: 'up' | 'down' | 'muted';
}

interface CountryVm { name: string; orders: number; pct: string; width: number; }
interface ShipVm { code: string; avg: string; width: number; }

interface TopVm {
  rank: number;
  first: boolean;
  prod: string;
  shop: string;
  country: string;
  qty: number;
  revenue: string;
  costLabel: string;
  profit: string;
  neg: boolean;
  margin: string;
  tone: 'green' | 'yellow' | 'red';
  width: number;
}

interface SelectOption { value: string; label: string; }

/** Magyar országnevek a gyakori ISO kódokhoz (design: 'Németország (DE)') */
const COUNTRY_NAMES: Record<string, string> = {
  DE: 'Németország', GB: 'Egyesült Királyság', US: 'Egyesült Államok',
  FR: 'Franciaország', AT: 'Ausztria', NL: 'Hollandia', BE: 'Belgium',
  IT: 'Olaszország', ES: 'Spanyolország', PT: 'Portugália', PL: 'Lengyelország',
  CZ: 'Csehország', SK: 'Szlovákia', HU: 'Magyarország', RO: 'Románia',
  DK: 'Dánia', SE: 'Svédország', FI: 'Finnország', NO: 'Norvégia',
  IE: 'Írország', CH: 'Svájc', LU: 'Luxemburg', SI: 'Szlovénia',
  HR: 'Horvátország', BG: 'Bulgária', GR: 'Görögország', EE: 'Észtország',
  LV: 'Lettország', LT: 'Litvánia', CA: 'Kanada', AU: 'Ausztrália',
  JP: 'Japán', MT: 'Málta', CY: 'Ciprus',
};

/** getDay() → magyar rövid nap (Hét nézet tengelyfeliratai) */
const WEEKDAY_SHORT = ['V', 'H', 'K', 'Sze', 'Cs', 'P', 'Szo'];
const MONTH_SHORT = ['Jan', 'Feb', 'Már', 'Ápr', 'Máj', 'Jún', 'Júl', 'Aug', 'Sze', 'Okt', 'Nov', 'Dec'];

const RANGE_TO_API: Record<RangeTab, ReportRange> = {
  'Nap': 'day', 'Hét': 'week', 'Hónap': 'month', 'Év': 'year', 'Egyedi': 'custom',
};

// SVG grafikon geometria (23-logic.js buildLineChart)
const W = 920, H = 300, PAD_L = 56, PAD_R = 20, PAD_T = 30, PAD_B = 34;
const IW = W - PAD_L - PAD_R, IH = H - PAD_T - PAD_B;

@Component({
  selector: 'app-riportok',
  templateUrl: './riportok.component.html',
  styleUrl: './riportok.component.scss'
})
export class RiportokComponent implements OnInit {

  readonly rangeTabs: RangeTab[] = ['Nap', 'Hét', 'Hónap', 'Év', 'Egyedi'];
  range: RangeTab = 'Hét';
  shop = 'Mind';
  country = 'Mind';
  customFrom = '';
  customTo = '';

  shopOptions: SelectOption[] = [{ value: 'Mind', label: 'Minden webshop' }];
  countryOptions: SelectOption[] = [{ value: 'Mind', label: 'Minden ország' }];

  loading = true;
  loadFailed = false;

  kpis: KpiVm[] = [];
  rangeLabel = '';
  chart: ChartVm | null = null;
  countries: CountryVm[] = [];
  countriesEmpty = false;
  shipRows: ShipVm[] = [];
  shipEmpty = false;
  topRows: TopVm[] = [];
  topEmpty = false;
  topNote = '';

  /** true, ha az időszakban egyáltalán nincs értékesítés (a grafikon lapos nullvonal lenne) */
  chartEmpty = false;

  hoverIdx: number | null = null;
  tooltip: TooltipVm | null = null;

  /** grafikon geometria a hover tooltiphez */
  private geo: { xs: number[]; ysS: number[]; ysP: number[] | null; bars: Bar[] } | null = null;
  private requestId = 0;

  constructor(
    private http: HttpClient,
    private reports: ReportsService,
  ) {
    // Egyedi időszak alapértelmezése: utolsó 14 nap
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 13);
    this.customFrom = this.isoDate(from);
    this.customTo = this.isoDate(today);
  }

  ngOnInit(): void {
    this.reload();
  }

  // --------------------------------------------------------------------------
  // Szűrők
  // --------------------------------------------------------------------------

  get isCustom(): boolean { return this.range === 'Egyedi'; }

  setRange(tab: RangeTab): void {
    if (this.range === tab) { return; }
    this.range = tab;
    this.reload();
  }

  onFilterChange(): void {
    this.reload();
  }

  onCustomDateChange(): void {
    if (this.isCustom) { this.reload(); }
  }

  // --------------------------------------------------------------------------
  // Betöltés
  // --------------------------------------------------------------------------

  reload(): void {
    const period = this.computePeriod();
    if (!period) {
      // Egyedi nézet érvénytelen dátumokkal (prototípus: 'válassz kezdő és záró dátumot')
      this.applyInvalidCustom();
      return;
    }
    const prev = this.computePrevPeriod(period);
    const id = ++this.requestId;

    this.loading = true;
    this.loadFailed = false;

    const overviewParams = (p: { from: Date; to: Date }) => new HttpParams()
      .set('from', this.isoDate(p.from))
      .set('to', this.isoDate(p.to))
      .set('archived', 'all');

    const filter: ReportFilter = { range: RANGE_TO_API[this.range] };
    if (this.isCustom) {
      filter.from = this.customFrom;
      filter.to = this.customTo;
    }
    if (this.shop !== 'Mind') { filter.shop = this.shop; }
    if (this.country !== 'Mind') { filter.country = this.country; }

    forkJoin({
      ov: this.http.get<OverviewResponse>('orders/reports/overview', { params: overviewParams(period) }),
      prevOv: this.http.get<OverviewResponse>('orders/reports/overview', { params: overviewParams(prev) })
        .pipe(catchError(() => of(null as OverviewResponse | null))),
      sales: this.reports.getSales(filter).pipe(catchError(() => of(null as ReportSales | null))),
      ship: this.reports.getShippingCosts(filter).pipe(catchError(() => of(null as ReportShippingCostRow[] | null))),
      top: this.reports.getTopProducts(filter).pipe(catchError(() => of(null as ReportTopProductRow[] | null))),
      countries: this.reports.getCountries(filter).pipe(catchError(() => of(null as ReportCountryRow[] | null))),
    }).subscribe({
      next: ({ ov, prevOv, sales, ship, top, countries }) => {
        if (id !== this.requestId) { return; }
        this.build(ov, prevOv, sales, ship, top, countries);
        this.loading = false;
      },
      error: () => {
        if (id !== this.requestId) { return; }
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  private applyInvalidCustom(): void {
    this.requestId++;
    this.loading = false;
    this.loadFailed = false;
    this.rangeLabel = 'válassz kezdő és záró dátumot';
    this.buildChart([{ l: '', s: 1, p: null }], false);
    this.kpis = [
      { icon: 'sales', label: 'Össz értékesítés', value: '—', delta: '', tone: 'muted' },
      { icon: 'profit', label: 'Tényleges profit', value: '—', delta: '', tone: 'muted' },
      { icon: 'orders', label: 'Rendelések', value: '—', delta: '', tone: 'muted' },
      { icon: 'basket', label: 'Átlag kosárérték', value: '—', delta: '', tone: 'muted' },
    ];
    this.countries = [];
    this.countriesEmpty = true;
    this.shipRows = [];
    this.shipEmpty = true;
    this.topRows = [];
    this.topEmpty = true;
    this.topNote = 'A profit a webshop díjak, a szállítás, a vám és a gyártási önköltség levonása után értendő.';
  }

  // --------------------------------------------------------------------------
  // Időszak számítás
  // --------------------------------------------------------------------------

  private computePeriod(): { from: Date; to: Date } | null {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    switch (this.range) {
      case 'Nap':
        return { from: new Date(today), to: new Date(today) };
      case 'Hét': {
        const monday = new Date(today);
        monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return { from: monday, to: sunday };
      }
      case 'Hónap':
        return {
          from: new Date(today.getFullYear(), today.getMonth(), 1),
          to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
        };
      case 'Év':
        return {
          from: new Date(today.getFullYear(), 0, 1),
          to: new Date(today.getFullYear(), 11, 31),
        };
      case 'Egyedi': {
        const from = this.parseDate(this.customFrom);
        const to = this.parseDate(this.customTo);
        if (!from || !to || to < from) { return null; }
        return { from, to };
      }
    }
  }

  private computePrevPeriod(period: { from: Date; to: Date }): { from: Date; to: Date } {
    const dayMs = 86400000;
    const days = Math.round((period.to.getTime() - period.from.getTime()) / dayMs) + 1;
    const prevTo = new Date(period.from);
    prevTo.setDate(prevTo.getDate() - 1);
    const prevFrom = new Date(prevTo);
    prevFrom.setDate(prevTo.getDate() - (days - 1));
    return { from: prevFrom, to: prevTo };
  }

  private parseDate(s: string): Date | null {
    const p = (s || '').split('-');
    if (p.length !== 3) { return null; }
    const d = new Date(+p[0], +p[1] - 1, +p[2]);
    return isNaN(d.getTime()) ? null : d;
  }

  private isoDate(d: Date): string {
    return d.getFullYear() + '-'
      + String(d.getMonth() + 1).padStart(2, '0') + '-'
      + String(d.getDate()).padStart(2, '0');
  }

  // --------------------------------------------------------------------------
  // Nézetmodell építés
  // --------------------------------------------------------------------------

  private build(
    ov: OverviewResponse,
    prevOv: OverviewResponse | null,
    sales: ReportSales | null,
    ship: ReportShippingCostRow[] | null,
    top: ReportTopProductRow[] | null,
    countries: ReportCountryRow[] | null,
  ): void {
    // ---- grafikon adatsor ----
    let bars: Bar[];
    let hasProfit = false;
    if (sales && sales.points && sales.points.length) {
      bars = sales.points.map(p => ({ l: p.label, s: p.sales || 0, p: p.profit || 0 }));
      hasProfit = true;
    } else {
      bars = this.barsFromTimeline(ov.timeline || []);
    }
    if (!bars.length) { bars = [{ l: '', s: 1, p: null }]; }
    bars = bars.slice(0, 92); // prototípus: max 92 nap

    this.rangeLabel = (sales && sales.rangeLabel) ? sales.rangeLabel : this.localRangeLabel(!!sales);
    this.buildChart(bars, hasProfit);

    // ---- KPI-k ----
    const filtered = this.shop !== 'Mind' || this.country !== 'Mind';
    const useSalesTotals = filtered && !!sales;
    const revenue = useSalesTotals ? (sales!.totalSales || 0) : (ov.totals?.revenue || 0);
    const orders = useSalesTotals ? (sales!.orderCount || 0) : (ov.totals?.orders || 0);
    const avgValue = useSalesTotals ? (sales!.avgOrderValue || 0) : (ov.totals?.average_order_value || 0);

    const revDelta = useSalesTotals ? null : this.deltaOf(revenue, prevOv?.totals?.revenue, ' előző időszakhoz');
    const ordDelta = useSalesTotals ? null : this.deltaOf(orders, prevOv?.totals?.orders);
    const avgDelta = useSalesTotals ? null : this.deltaOf(avgValue, prevOv?.totals?.average_order_value);

    const totalProfit = sales ? (sales.totalProfit || 0) : null;
    const marginPct = sales
      ? (sales.marginPct != null
        ? Math.round(sales.marginPct)
        : ((sales.totalSales || 0) > 0 ? Math.round((sales.totalProfit || 0) / sales.totalSales * 100) : 0))
      : null;

    this.kpis = [
      {
        icon: 'sales', label: 'Össz értékesítés', value: this.fmt(revenue),
        delta: revDelta ? revDelta.text : '—',
        tone: revDelta ? (revDelta.up ? 'up' : 'down') : 'muted',
      },
      {
        icon: 'profit', label: 'Tényleges profit',
        value: totalProfit != null ? this.fmt(totalProfit) : '—',
        // Forgalom nélküli időszakra a „0% árrés" félrevezető — ott nincs mihez viszonyítani.
        delta: (marginPct != null && revenue > 0) ? marginPct + '% árrés' : 'nincs adat',
        tone: (totalProfit != null && revenue > 0) ? (totalProfit >= 0 ? 'up' : 'down') : 'muted',
      },
      {
        icon: 'orders', label: 'Rendelések', value: this.thousands(orders),
        delta: ordDelta ? ordDelta.text : '—',
        tone: ordDelta ? (ordDelta.up ? 'up' : 'down') : 'muted',
      },
      {
        icon: 'basket', label: 'Átlag kosárérték', value: this.fmt(avgValue),
        delta: avgDelta ? avgDelta.text : '—',
        tone: avgDelta ? (avgDelta.up ? 'up' : 'down') : 'muted',
      },
    ];

    // ---- szűrő opciók a valós adatból ----
    this.mergeShopOptions(ov.merchants || []);
    this.mergeCountryOptions(ov.countries || []);

    // ---- rendelések ország szerint ----
    // Elsődlegesen a reports/countries végpont (ez követi a webshop/ország
    // szűrőt); ha nem elérhető, az overview (szűretlen) bontása a fallback.
    if (countries) {
      const total = countries.reduce((sum, c) => sum + (c.orders || 0), 0);
      const rows = countries.slice(0, 5);
      const maxOrders = Math.max(...rows.map(c => c.orders || 0), 1);
      this.countries = rows.map(c => ({
        name: c.countryName || this.countryName(c.countryCode),
        orders: c.orders || 0,
        pct: (total > 0 ? Math.round((c.orders || 0) / total * 100) : 0) + '%',
        width: Math.round((c.orders || 0) / maxOrders * 100),
      }));
    } else {
      const countryRows = (ov.countries || []).slice(0, 5);
      const maxOrders = Math.max(...countryRows.map(c => c.order_count), 1);
      this.countries = countryRows.map(c => ({
        name: this.countryName(c.country),
        orders: c.order_count,
        pct: Math.round(c.share || 0) + '%',
        width: Math.round(c.order_count / maxOrders * 100),
      }));
    }
    this.countriesEmpty = this.countries.length === 0;

    // ---- szállítás ország szerint ----
    const shipRows = ship || [];
    const maxAvg = Math.max(...shipRows.map(s => s.avgCost || 0), 0.01);
    this.shipRows = shipRows.map(s => ({
      code: s.countryCode,
      avg: this.eur2(s.avgCost || 0),
      width: Math.round((s.avgCost || 0) / maxAvg * 100),
    }));
    this.shipEmpty = this.shipRows.length === 0;

    // ---- legjövedelmezőbb termékek ----
    const topRows = (top || []).slice().sort((a, b) => (b.profit || 0) - (a.profit || 0));
    this.topRows = topRows.map((r, i) => {
      const margin = Math.round(r.marginPct || 0);
      return {
        rank: i + 1,
        first: i === 0,
        prod: r.productName,
        shop: r.shop,
        country: r.countryCode,
        qty: r.quantity || 0,
        revenue: this.eurR(r.revenue || 0),
        costLabel: 'díj ' + this.eur2(r.fee || 0)
          + ' · száll. ' + this.eur2(r.shippingCost || 0)
          + ((r.duty || 0) > 0 ? ' · vám ' + this.eur2(r.duty) : '')
          + ' · gyártás ' + this.eur2(r.productionCost || 0),
        profit: this.eurR(r.profit || 0),
        neg: (r.profit || 0) <= 0,
        margin: margin + '%',
        tone: margin >= 35 ? 'green' : (margin >= 20 ? 'yellow' : 'red'),
        width: Math.max(4, Math.min(100, margin * 2)),
      } as TopVm;
    });
    this.topEmpty = this.topRows.length === 0;
    const usRow = topRows.find(r => r.countryCode === 'US');
    this.topNote = usRow
      ? 'A US kombinációknál a 10% vám és a drága szállítás miatt az árrés jóval alacsonyabb — bevételre nagynak tűnhet, profitra nem az.'
      : 'A profit a webshop díjak, a szállítás, a vám és a gyártási önköltség levonása után értendő.';
  }

  /** overview.timeline → grafikon adatsor, amíg a reports/sales végpont nem él (profit nélkül) */
  private barsFromTimeline(timeline: OverviewResponse['timeline']): Bar[] {
    const rows = timeline || [];
    switch (this.range) {
      case 'Nap':
        return rows.map(t => ({ l: 'Ma', s: t.revenue || 0, p: null }));
      case 'Hét':
        return rows.map(t => {
          const d = this.parseDate(t.date);
          return { l: d ? WEEKDAY_SHORT[d.getDay()] : '', s: t.revenue || 0, p: null };
        });
      case 'Hónap':
        return rows.map(t => {
          const d = this.parseDate(t.date);
          return { l: d ? String(d.getDate()) : '', s: t.revenue || 0, p: null };
        });
      case 'Év': {
        const months: Bar[] = MONTH_SHORT.map(m => ({ l: m, s: 0, p: null }));
        rows.forEach(t => {
          const d = this.parseDate(t.date);
          if (d) { months[d.getMonth()].s += t.revenue || 0; }
        });
        return months;
      }
      case 'Egyedi':
        return rows.map(t => {
          const d = this.parseDate(t.date);
          return { l: d ? d.getDate() + '.' + (d.getMonth() + 1) + '.' : '', s: t.revenue || 0, p: null };
        });
    }
  }

  /** időszak felirat a prototípus szövegeivel */
  private localRangeLabel(hourly: boolean): string {
    const today = new Date();
    switch (this.range) {
      case 'Nap': return hourly ? 'ma, óránként' : 'ma';
      case 'Hét': return 'ez a hét, naponta';
      case 'Hónap': {
        const days = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        return 'ez a hónap, naponta (' + days + ' nap)';
      }
      case 'Év': return today.getFullYear() + ', havonta';
      case 'Egyedi': {
        const from = this.parseDate(this.customFrom);
        const to = this.parseDate(this.customTo);
        if (!from || !to) { return 'válassz kezdő és záró dátumot'; }
        const f2 = (d: Date) => d.getFullYear() + '. '
          + String(d.getMonth() + 1).padStart(2, '0') + '. '
          + String(d.getDate()).padStart(2, '0') + '.';
        const nDays = Math.min(92, Math.round((to.getTime() - from.getTime()) / 86400000) + 1);
        return f2(from) + ' – ' + f2(to) + ' (' + nDays + ' nap)';
      }
    }
  }

  private mergeShopOptions(merchants: OverviewResponse['merchants']): void {
    const known = new Set(this.shopOptions.map(o => o.value));
    merchants.forEach(m => {
      const name = (m.merchant_name || '').trim();
      if (name && !known.has(name)) {
        known.add(name);
        this.shopOptions.push({ value: name, label: name });
      }
    });
  }

  private mergeCountryOptions(countries: OverviewResponse['countries']): void {
    const known = new Set(this.countryOptions.map(o => o.value));
    countries.forEach(c => {
      const raw = (c.country || '').trim();
      if (!raw || raw === 'Ismeretlen') { return; }
      const value = /^[A-Za-z]{2}$/.test(raw) ? raw.toUpperCase() : raw;
      if (!known.has(value)) {
        known.add(value);
        this.countryOptions.push({ value, label: value });
      }
    });
  }

  private countryName(raw: string): string {
    const v = (raw || '').trim();
    if (/^[A-Za-z]{2}$/.test(v)) {
      const code = v.toUpperCase();
      const name = COUNTRY_NAMES[code];
      return name ? name + ' (' + code + ')' : code;
    }
    return v || 'Ismeretlen';
  }

  private deltaOf(current: number, previous: number | undefined | null, suffix = ''): { text: string; up: boolean } | null {
    if (previous == null || previous <= 0) { return null; }
    const pct = Math.round((current - previous) / previous * 100);
    const up = pct >= 0;
    return { text: (up ? '▲ ' : '▼ ') + Math.abs(pct) + '%' + suffix, up };
  }

  // --------------------------------------------------------------------------
  // SVG vonaldiagram (23-logic.js buildLineChart 1:1)
  // --------------------------------------------------------------------------

  private buildChart(bars: Bar[], hasProfit: boolean): void {
    this.hoverIdx = null;
    this.tooltip = null;
    this.chartEmpty = !bars.some(b => (b.s || 0) > 0);

    const n = bars.length;
    const maxS = Math.max(...bars.map(b => b.s), 1);
    const yTop = Math.ceil(maxS / 100) * 100 * 1.08;
    const x = (i: number) => PAD_L + (n === 1 ? IW / 2 : IW * i / (n - 1));
    const y = (v: number) => PAD_T + IH - (v / yTop) * IH;

    // rácsvonalak + Y tengely euró
    const gridN = 4;
    const grid: ChartVm['grid'] = [];
    for (let g = 0; g <= gridN; g++) {
      const val = yTop * g / gridN;
      grid.push({ y: this.r1(y(val)), label: this.fmt(val), base: g === 0 });
    }

    // sima (catmull-rom → bezier) vonal
    const smoothPath = (vals: number[]): string => {
      const p = vals.map((v, i) => [x(i), y(v)] as [number, number]);
      if (p.length < 2) { return 'M' + (p[0] ? p[0][0].toFixed(1) + ' ' + p[0][1].toFixed(1) : '0 0'); }
      return p.map((pt, i) => {
        if (i === 0) { return 'M' + pt[0].toFixed(1) + ' ' + pt[1].toFixed(1); }
        const p0 = p[i - 1], pm = p[i - 2] || p0, pn = p[i + 1] || pt;
        const c1x = p0[0] + (pt[0] - pm[0]) / 6, c1y = p0[1] + (pt[1] - pm[1]) / 6;
        const c2x = pt[0] - (pn[0] - p0[0]) / 6, c2y = pt[1] - (pn[1] - p0[1]) / 6;
        return 'C' + c1x.toFixed(1) + ' ' + c1y.toFixed(1) + ',' + c2x.toFixed(1) + ' ' + c2y.toFixed(1) + ',' + pt[0].toFixed(1) + ' ' + pt[1].toFixed(1);
      }).join(' ');
    };

    const salesVals = bars.map(b => b.s);
    const profitVals = hasProfit ? bars.map(b => b.p || 0) : null;

    // x-címkék: sűrű sorozatnál ritkítás
    const step = n > 16 ? Math.ceil(n / 12) : 1;
    const xLabels: TextEl[] = [];
    bars.forEach((b, i) => {
      if ((i % step === 0 || i === n - 1) && b.l) {
        xLabels.push({ x: this.r1(x(i)), y: H - 12, text: b.l });
      }
    });

    // érték- és profit% címkék — csak ritka nézetnél
    const showPct = n <= 12;
    const salesLabels: TextEl[] = [];
    const pctLabels: TextEl[] = [];
    const salesDots: Pt[] = [];
    const profitDots: Pt[] = [];
    if (showPct) {
      bars.forEach((b, i) => {
        const ys = this.r1(y(b.s));
        salesLabels.push({ x: this.r1(x(i)), y: this.r1(ys - 9), text: this.fmt(b.s) });
        salesDots.push({ x: this.r1(x(i)), y: ys });
        if (hasProfit) {
          const p = b.p || 0;
          const yp = this.r1(y(p));
          profitDots.push({ x: this.r1(x(i)), y: yp });
          // Forgalom nélküli napra a „0%" árrés félrevezető, ráadásul pont a „0 €"
          // értékcímkére esne (azonos pont) — ilyenkor nem rajzoljuk ki.
          if (b.s > 0) {
            const pct = Math.round(p / b.s * 100);
            // Ha a profit-pont a sales-ponthoz tapad, a % címke a pont ALÁ kerül,
            // különben a két felirat egymásra íródna.
            const collides = Math.abs(yp - ys) < 14;
            pctLabels.push({ x: this.r1(x(i)), y: this.r1(collides ? yp + 16 : yp - 9), text: pct + '%' });
          }
        }
      });
    }

    // utolsó pont kiemelése sűrű nézetnél
    const lastBar = bars[n - 1];
    const last = (!showPct && n > 1)
      ? {
        x: this.r1(x(n - 1)),
        ys: this.r1(y(lastBar.s)),
        yp: hasProfit ? this.r1(y(lastBar.p || 0)) : null,
      }
      : null;

    // láthatatlan hover-sávok
    const hitW = IW / n;
    const hits = bars.map((_, i) => ({ x: this.r1(PAD_L + hitW * i), w: this.r1(hitW) }));

    this.chart = {
      grid,
      salesPath: smoothPath(salesVals),
      profitPath: profitVals ? smoothPath(profitVals) : null,
      salesDots,
      profitDots,
      salesLabels,
      pctLabels,
      xLabels,
      last,
      hits,
    };
    this.geo = {
      xs: bars.map((_, i) => this.r1(x(i))),
      ysS: bars.map(b => this.r1(y(b.s))),
      ysP: hasProfit ? bars.map(b => this.r1(y(b.p || 0))) : null,
      bars,
    };
  }

  setHover(i: number | null): void {
    this.hoverIdx = i;
    if (i == null || !this.geo || i < 0 || i >= this.geo.bars.length) {
      this.tooltip = null;
      return;
    }
    const b = this.geo.bars[i];
    const hx = this.geo.xs[i];
    const sy = this.geo.ysS[i];
    const py = this.geo.ysP ? this.geo.ysP[i] : null;
    const tw = 132, th = 66;
    const tx = Math.max(PAD_L, Math.min(W - PAD_R - tw, hx - tw / 2));
    const topY = py != null ? Math.min(sy, py) : sy;
    const ty = Math.max(0, topY - th - 12);
    const pct = (b.p != null && b.s > 0) ? Math.round(b.p / b.s * 100) : null;
    this.tooltip = {
      x: hx, sy, py, tx: this.r1(tx), ty: this.r1(ty),
      label: b.l,
      salesText: 'Elad.: ' + this.fmt(b.s),
      profitText: b.p != null
        ? 'Profit: ' + this.fmt(b.p) + (pct != null ? ' · ' + pct + '%' : '')
        : 'Profit: —',
    };
  }

  // --------------------------------------------------------------------------
  // Formázók (23-logic.js)
  // --------------------------------------------------------------------------

  /** 1000 felett '14,0k €', alatta '620 €' */
  private fmt(n: number): string {
    return n >= 1000
      ? (n / 1000).toFixed(1).replace('.', ',') + 'k €'
      : Math.round(n) + ' €';
  }

  private eur2(n: number): string {
    return (Math.round(n * 100) / 100).toFixed(2).replace('.', ',') + ' €';
  }

  private eurR(n: number): string {
    return Math.round(n) + ' €';
  }

  private thousands(n: number): string {
    return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  private r1(n: number): number {
    return Math.round(n * 10) / 10;
  }
}
